import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import type { ToolExecutionContext } from '../types';
import { resolveSceneGroupHandle } from './scene-group-addressing';
import { VideoStateTool } from './VideoStateTool';

import { AudioVolume } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { makeOperationsCallable } from 'js/src/luminary/luminary-hooks';
import { setMusicVolumeOverrideOperation } from 'js/src/luminary/luminary-operations';
import {
    getVoicedrivenBaseAssetVolume,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';
import { setVoicedrivenBaseAssetVolumeOperation } from 'js/src/luminary/media-operations';

/** `music_volume` value that removes the group's override and returns to automatic ducking. */
export const AUTO_MUSIC_VOLUME = 'auto';

export const setSceneGroupVolumeSchema = z.object({
    scene_group_id: z
        .string()
        .describe('The stable scene_group_id of the scene group to adjust (from the video state).'),
    voice_volume: z
        .number()
        .min(AudioVolume.MIN)
        .max(AudioVolume.MAX)
        .optional()
        .describe(
            "Volume of the group's voice — the talking-head recording, AI avatar or voiceover track — from 0 (silent) to 100 (full). Voice-driven scene groups only.",
        ),
    music_volume: z
        .union([z.number().min(AudioVolume.MIN).max(AudioVolume.MAX), z.literal(AUTO_MUSIC_VOLUME)])
        .optional()
        .describe(
            `Background music level while this scene group plays, from 0 (silent) to 100 (full), overriding the video's music volume for just this group; or "auto" to remove the override (the music then ducks to ${AudioVolume.DUCK} under a voice, and otherwise follows the video's music volume). Use it to duck or mute the music under one group without touching the rest of the video.`,
        ),
});
export type SetSceneGroupVolumeArgs = z.infer<typeof setSceneGroupVolumeSchema>;

/**
 * Sets the audio mix of one scene group: the voice level (a talking-head recording's or voiceover
 * track's volume, which lives on the scene group rather than on any scene's asset) and the music
 * level while the group plays (per-group ducking). Mirrors the editor's per-group "Volume controls"
 * dropdown. Whole-video music volume is `set_music_volume`; a b-roll clip's own volume is
 * `edit_media`.
 */
export class SetSceneGroupVolumeTool extends VideoStateTool<typeof setSceneGroupVolumeSchema> {
    protected override readonly schema = setSceneGroupVolumeSchema;
    protected override readonly toolName = TOOL_NAMES.setSceneGroupVolume;
    protected override readonly toolDescription = `Set the audio mix of one scene group: voice_volume for the level of its talking-head recording, AI avatar or voiceover track, and/or music_volume for how loud the background music plays under just this group (per-group ducking). Use this — not ${TOOL_NAMES.editMedia} — for the speaker's or narration's volume; the voice belongs to the scene group, not to a scene's media slot. ${TOOL_NAMES.setMusicVolume} sets the music for the whole video; ${TOOL_NAMES.editMedia} sets a b-roll clip's own volume.`;

    public override getToolDisplayName(): string {
        return 'Setting scene group volume';
    }

    public get statusIcon(): string {
        return 'volume_up';
    }

    public async execute(args: SetSceneGroupVolumeArgs, context: ToolExecutionContext) {
        const { voice_volume, music_volume } = args;
        const { parentActionGroup } = context;

        if (voice_volume === undefined && music_volume === undefined) {
            return 'Error: Nothing to change. Pass voice_volume and/or music_volume.';
        }

        const { sceneGroup, sceneGroupShortId } = resolveSceneGroupHandle(this.luminary, args);

        if (voice_volume !== undefined && !isVoiceDrivenSceneGroup(sceneGroup)) {
            return `Error: Scene group ${sceneGroupShortId} has no voice (it is not a voiceover or talking-head group), so voice_volume does not apply. To change the volume of a video clip on one of its scenes, use ${TOOL_NAMES.editMedia} with volume.`;
        }

        const { setVoicedrivenBaseAssetVolume, setMusicVolumeOverride } = makeOperationsCallable(
            { setVoicedrivenBaseAssetVolumeOperation, setMusicVolumeOverrideOperation },
            this.luminary,
            this.dispatch,
            this.getState as () => RootState,
        );

        const changes: string[] = [];
        const unchanged: string[] = [];
        try {
            if (voice_volume !== undefined) {
                if (getVoicedrivenBaseAssetVolume(sceneGroup) === voice_volume) {
                    unchanged.push(`the voice volume is already ${voice_volume}%`);
                } else {
                    // Both operations checkpoint under the shared parentActionGroup, so a call that
                    // sets both levels is still a single undo step.
                    setVoicedrivenBaseAssetVolume(
                        sceneGroup.id,
                        voice_volume,
                        false,
                        parentActionGroup,
                    );
                    changes.push(`voice volume to ${voice_volume}%`);
                }
            }

            if (music_volume !== undefined) {
                const override = music_volume === AUTO_MUSIC_VOLUME ? null : music_volume;
                if (sceneGroup.musicVolumeOverride === override) {
                    unchanged.push(
                        override === null
                            ? 'the music volume is already automatic'
                            : `the music volume is already ${override}%`,
                    );
                } else {
                    setMusicVolumeOverride(sceneGroup.id, override, false, parentActionGroup);
                    changes.push(
                        override === null
                            ? 'music volume to automatic'
                            : `music volume to ${override}% while this group plays`,
                    );
                }
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
            return `Error setting scene group volume: ${errorMessage}`;
        }

        if (changes.length === 0) {
            return `No change: ${unchanged.join(' and ')} for scene group ${sceneGroupShortId}.`;
        }
        const unchangedNote = unchanged.length > 0 ? ` (${unchanged.join('; ')})` : '';
        return `Success: Set ${changes.join(' and ')} for scene group ${sceneGroupShortId}.${unchangedNote}`;
    }
}
